'use client';

import PropTypes from 'prop-types';
import cx from 'classnames';
import {useEffect, useMemo} from 'react';
import {Button} from '../atoms/Button';
import {Typography} from '../atoms/Typography';
import {Overlay} from '../atoms/Overlay';
import {Spinner} from '../atoms/Spinner';

export {Spinner};

// Intended for small, single-action content, returns a popup with overlay
// in both desktop & mobile.
// Requires isOpen, onClose, as well as the following:

// Requires either children:
//    (will render children with X close button in upper right in an otherwise empty modal)
// OR: all the below
//  title: (node),
//  body: (node),
//  primaryAction: (func called on primary CTA),
//  modalId: (string identifier for this modal)
//  primaryText: (string for primary CTA text)

export const ResponsiveModal = ({
  isOpen,
  body = null,
  children = null,
  onClose,
  primaryAction = onClose,
  primaryActionDisabled = false,
  secondaryAction = onClose,
  secondaryActionDisabled = false,
  primaryText = 'Submit',
  secondaryText = 'Cancel',
  showSpinner = false,
  actionText = 'One moment please...',
  showSecondary = true,
  modalId = 'modal',
  className = '',
  isModal = false,
  showHeader = true,
  showPrimaryButton = true,
  title = null,
  closeOnClickOutside,
  primaryQaId = null,
  secondaryQaId = null,
  closeQaId = null,
  titleQaId = null,
  bodyQaId = null,
  secondaryImpressionTracking = null,
  primaryImpressionTracking = null,
  equalButtons,
  overlayClassName = '',
}) => {
  const primaryQaIdToUse = primaryQaId ?? `${modalId}-submit`;
  const secondaryQaIdToUse = secondaryQaId ?? `${modalId}-cancel`;

  useEffect(() => {
    if (isOpen) {
      window.setTimeout(() => document.body.classList.add('no-scroll'), 100);
    } else {
      window.setTimeout(() => document.body.classList.remove('no-scroll'), 100);
    }

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [isOpen]);

  const renderedTitle = useMemo(
    () => (showSpinner ? actionText : title || `${primaryText}?` || 'Are you sure?'),
    [showSpinner, actionText, title, primaryText]
  );

  let titleText = null;
  if (showHeader) {
    if (showSpinner) titleText = actionText;
    else titleText = renderedTitle;
  }

  let renderedChildren = showSpinner ? <Spinner center /> : children;

  const getModalChildren = () => (
    <div className={className}>
      <div className="responsive-modal-body">
        {typeof body !== 'string' ? (
          body
        ) : (
          <Typography variant="paragraph2" data-qa-id={bodyQaId ?? ''}>
            {body || ''}
          </Typography>
        )}
      </div>
      <div className="responsive-modal-button-row">
        {showSecondary && (
          <Button
            variant="secondary"
            size="large"
            onClick={secondaryAction}
            data-qa-id={secondaryQaIdToUse}
            data-impression-tracking={secondaryImpressionTracking}
            fullWidth={equalButtons}
            disabled={secondaryActionDisabled}
          >
            {secondaryText}
          </Button>
        )}
        {showPrimaryButton && (
          <Button
            variant="primary"
            size="large"
            onClick={primaryAction}
            data-qa-id={primaryQaIdToUse}
            data-impression-tracking={primaryImpressionTracking}
            fullWidth={equalButtons}
            disabled={primaryActionDisabled}
          >
            {primaryText}
          </Button>
        )}
      </div>
    </div>
  );

  if (!renderedChildren) renderedChildren = getModalChildren();

  return (
    <Overlay
      open={isOpen}
      isModal={isModal}
      onClose={(e) => onClose(e)}
      modalId={cx('responsive-modal', modalId)}
      title={titleText}
      showSpinner={showSpinner}
      closeOnClickOutside={closeOnClickOutside}
      titleQaId={titleQaId}
      closeQaId={closeQaId}
      className={overlayClassName}
    >
      {renderedChildren}
    </Overlay>
  );
};

ResponsiveModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
  title: PropTypes.node,
  body: PropTypes.node,
  modalId: PropTypes.string,
  primaryAction: PropTypes.func,
  primaryText: PropTypes.string,
  secondaryAction: PropTypes.func,
  secondaryText: PropTypes.string,
  actionText: PropTypes.string,
  showSpinner: PropTypes.bool,
  showHeader: PropTypes.bool,
  showPrimaryButton: PropTypes.bool,
  showSecondary: PropTypes.bool,
  className: PropTypes.string,
  primaryQaId: PropTypes.string,
  secondaryQaId: PropTypes.string,
  closeQaId: PropTypes.string,
  titleQaId: PropTypes.string,
  bodyQaId: PropTypes.string,
  equalButtons: PropTypes.bool,
  overlayClassName: PropTypes.string,
};
